// components/layout/BlitzPayHeader.jsx - Clean Mobile Design
import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  useMediaQuery,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Receipt as ReceiptIcon,
  Settings as SettingsIcon,
  MoreVert as MoreVertIcon,
  SwapHoriz as TransferIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  PersonAdd as PersonAddIcon,
  AttachMoney as AttachMoneyIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTheme, alpha } from '@mui/material/styles';
import client from '../../../API';
import { CallSplit as CallSplitIcon } from "@mui/icons-material";
import { HeaderSection, StyledBadge } from "./styles";

export const SPLIT_YELLOW = "#FFC400";
export const SPLIT_YELLOW_DARK = "#E6AC00";
export const SPLIT_INK = "#1A1A0E"; // text on yellow — never white on yellow

// Internal brand red for BlitzPay
export const BLITZ_RED = "#E53935";
export const BLITZ_RED_DARK = "#C62828";

// Blue for Transfer
export const TRANSFER_BLUE = "#1E88E5";
export const TRANSFER_BLUE_DARK = "#1565C0";

/** Robust clipboard copy with Safari + legacy fallbacks */
async function copyTextToClipboard(text) {
  try {
    if (
      typeof navigator !== 'undefined' &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === 'function' &&
      window.isSecureContext &&
      document.hasFocus()
    ) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }

  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    if (ok) return true;
  } catch { /* fall through */ }

  window.prompt('Copy this invite link:', text);
  return false;
}

const BlitzPayHeader = ({
  totalIncomingAmount,
  spendingInfo,
  companyInfo,
  onCopyLink,
  onOpenVendorDialog
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const APPSHELL_HEIGHT = 86;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [vendorMenuAnchor, setVendorMenuAnchor] = useState(null);

  // Creator referral invite link state
  const [inviteCopied, setInviteCopied] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);

  const hasBlitzPayAccess = companyInfo?.blitzpay_account !== false;
  const hasAvailableFunds = spendingInfo && parseFloat(spendingInfo.total_available_funds) > 0;

  const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);
  const handleDrawerAction = (action) => {
    if (action) action();
    setDrawerOpen(false);
  };

  const handleVendorMenuOpen = (event) => setVendorMenuAnchor(event.currentTarget);
  const handleVendorMenuClose = () => setVendorMenuAnchor(null);
  const handleVendorMenuAction = (action) => {
    if (action) action();
    handleVendorMenuClose();
  };

  /**
   * Calls client.globalUsers.copyInvite('creator') — the exact same API
   * used by InviteLinkCard in MyReferrals — then copies the returned URL.
   */
  const handleCopyCreatorInvite = async () => {
    if (inviteLoading) return;
    try {
      setInviteLoading(true);
      const result = await client.globalUsers.copyInvite('creator');
      const inviteUrl =
        result?.invite_url   ||
        result?.inviteUrl    ||
        result?.data?.invite_url  ||
        result?.data?.inviteUrl   ||
        '';
      if (!inviteUrl) throw new Error('No invite URL returned');
      await copyTextToClipboard(inviteUrl);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2500);
    } catch (err) {
      console.error('Failed to copy creator invite link', err);
    } finally {
      setInviteLoading(false);
    }
  };

  // ── Desktop ───────────────────────────────────────────────────────────────
  const renderDesktopActions = () => (
    <>
      <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-start', alignItems: 'center' }}>
        {/* 1 — Transfer (blue) */}
        <Tooltip title="Transfer your balance between Blitz users" placement="top" arrow>
          <Button
            variant="contained"
            startIcon={<TransferIcon />}
            onClick={() => navigate("/blitzpay/internal-transfer")}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.5,
              bgcolor: TRANSFER_BLUE,
              color: '#fff',
              boxShadow: "0 2px 12px rgba(30,136,229,0.35)",
              "&:hover": {
                bgcolor: TRANSFER_BLUE_DARK,
                boxShadow: "0 4px 16px rgba(30,136,229,0.45)",
              },
            }}
          >
            Transfer
          </Button>
        </Tooltip>

        {/* 2 — BlitzPay (internal red), second when available */}
        {hasBlitzPayAccess && (
          <Tooltip title={!hasAvailableFunds ? "No funds available" : ""} arrow>
            <span>
              <Button
                variant="contained"
                startIcon={<AttachMoneyIcon />}
                onClick={() => navigate("/pay")}
                disabled={!hasAvailableFunds}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 3,
                  py: 1.5,
                  bgcolor: BLITZ_RED,
                  color: '#fff',
                  boxShadow: "0 2px 12px rgba(229,57,53,0.35)",
                  "&:hover": {
                    bgcolor: BLITZ_RED_DARK,
                    boxShadow: "0 4px 16px rgba(229,57,53,0.45)",
                  },
                  "&.Mui-disabled": {
                    bgcolor: alpha(BLITZ_RED, 0.3),
                    color: alpha('#fff', 0.6),
                  },
                }}
              >
                BlitzPay
              </Button>
            </span>
          </Tooltip>
        )}

        {/* 3 — Split (yellow) */}
        <Tooltip title="Invoice a BlitzPay company with your contractors on it" placement="top" arrow>
          <Button
            variant="contained"
            startIcon={<CallSplitIcon />}
            onClick={() => navigate("/split")}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              px: 3,
              py: 1.5,
              bgcolor: SPLIT_YELLOW,
              color: SPLIT_INK,
              boxShadow: "0 2px 12px rgba(255,196,0,0.35)",
              "&:hover": {
                bgcolor: SPLIT_YELLOW_DARK,
                boxShadow: "0 4px 16px rgba(255,196,0,0.45)",
              },
            }}
          >
            Split
          </Button>
        </Tooltip>

        {/* 4 — Invite Creators — uses referral invite link, NOT vendor link */}
        <Tooltip
          title={inviteCopied ? "Copied!" : "Copy your creator referral invite link"}
          placement="top"
          arrow
        >
          <span>
            <Button
              variant="outlined"
              color={inviteCopied ? "success" : "primary"}
              startIcon={inviteCopied ? <CheckIcon /> : <PersonAddIcon />}
              onClick={handleCopyCreatorInvite}
              disabled={inviteLoading}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                borderColor: inviteCopied
                  ? theme.palette.success.main
                  : alpha(theme.palette.primary.main, 0.5),
                color: inviteCopied ? 'success.main' : 'primary.main',
                bgcolor: inviteCopied ? alpha(theme.palette.success.main, 0.06) : 'transparent',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: inviteCopied ? theme.palette.success.main : theme.palette.primary.main,
                  bgcolor: inviteCopied
                    ? alpha(theme.palette.success.main, 0.1)
                    : alpha(theme.palette.primary.main, 0.06),
                },
              }}
            >
              {inviteLoading ? 'Getting link…' : inviteCopied ? 'Link Copied!' : 'Invite'}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {/* Vendor menu (unchanged) */}
      <Menu
        anchorEl={vendorMenuAnchor}
        open={Boolean(vendorMenuAnchor)}
        onClose={handleVendorMenuClose}
        onClick={handleVendorMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              mt: 1.5, minWidth: 280, maxWidth: 320,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              borderRadius: 2.5, border: '1px solid', borderColor: 'divider',
              overflow: 'visible',
              '&:before': {
                content: '""', display: 'block', position: 'absolute',
                top: 0, left: '50%', width: 12, height: 12,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) translateX(-50%) rotate(45deg)',
                zIndex: 0, borderLeft: '1px solid', borderTop: '1px solid', borderColor: 'divider',
              },
            }
          }
        }}
      >
        <MenuItem onClick={() => handleVendorMenuAction(onCopyLink)} sx={{ py: 1.75, px: 2.5, borderRadius: 1.5, mx: 1, my: 0.5 }}>
          <ListItemIcon sx={{ minWidth: 44 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ContentCopyIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            </Box>
          </ListItemIcon>
          <ListItemText
            primary="Copy Vendor Link"
            secondary="Share signup link"
            primaryTypographyProps={{ fontWeight: 600, fontSize: '0.938rem' }}
            secondaryTypographyProps={{ fontSize: '0.813rem' }}
          />
        </MenuItem>
        <MenuItem onClick={() => handleVendorMenuAction(onOpenVendorDialog)} sx={{ py: 1.75, px: 2.5, borderRadius: 1.5, mx: 1, my: 0.5 }}>
          <ListItemIcon sx={{ minWidth: 44 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PersonAddIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            </Box>
          </ListItemIcon>
          <ListItemText
            primary="Add Vendor Manually"
            secondary="Enter vendor details"
            primaryTypographyProps={{ fontWeight: 600, fontSize: '0.938rem' }}
            secondaryTypographyProps={{ fontSize: '0.813rem' }}
          />
        </MenuItem>
      </Menu>
    </>
  );

  // ── Mobile quick actions ───────────────────────────────────────────────────
  const renderMobileQuickActions = () => (
    <Box sx={{ width: '100%', mt: 1.5 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1}>
          {/* 1 — Transfer (blue) */}
          <Button
            variant="contained"
            startIcon={<TransferIcon sx={{ fontSize: 18 }} />}
            onClick={() => navigate("/blitzpay/internal-transfer")}
            sx={{
              flex: 1, py: 1.25, borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.813rem',
              bgcolor: TRANSFER_BLUE,
              color: '#fff',
              "&:hover": { bgcolor: TRANSFER_BLUE_DARK },
            }}
          >
            Transfer
          </Button>

          {/* 2 — BlitzPay (internal red), second when available */}
          {hasBlitzPayAccess && (
            <Button
              variant="contained"
              startIcon={<AttachMoneyIcon sx={{ fontSize: 18 }} />}
              onClick={() => navigate("/pay")}
              disabled={!hasAvailableFunds}
              sx={{
                flex: 1, py: 1.25, borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: '0.813rem',
                bgcolor: BLITZ_RED,
                color: '#fff',
                "&:hover": { bgcolor: BLITZ_RED_DARK },
                "&.Mui-disabled": {
                  bgcolor: alpha(BLITZ_RED, 0.3),
                  color: alpha('#fff', 0.6),
                },
              }}
            >
              BlitzPay
            </Button>
          )}
        </Stack>

        <Stack direction="row" spacing={1}>
          {/* 3 — Split (yellow) */}
          <Button
            variant="contained"
            startIcon={<CallSplitIcon sx={{ fontSize: 18 }} />}
            onClick={() => navigate("/split")}
            sx={{
              flex: 1, py: 1.25, borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: '0.813rem',
              bgcolor: SPLIT_YELLOW,
              color: SPLIT_INK,
              "&:hover": { bgcolor: SPLIT_YELLOW_DARK },
            }}
          >
            Split
          </Button>

          {/* 4 — Invite Creators mobile — referral link */}
          <Button
            variant="outlined"
            color={inviteCopied ? "success" : "primary"}
            startIcon={inviteCopied ? <CheckIcon sx={{ fontSize: 18 }} /> : <PersonAddIcon sx={{ fontSize: 18 }} />}
            onClick={handleCopyCreatorInvite}
            disabled={inviteLoading}
            sx={{
              flex: 1, py: 1.25, borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.813rem',
              borderColor: inviteCopied ? theme.palette.success.main : alpha(theme.palette.primary.main, 0.5),
              color: inviteCopied ? 'success.main' : 'primary.main',
              bgcolor: inviteCopied ? alpha(theme.palette.success.main, 0.06) : 'transparent',
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {inviteLoading ? '…' : inviteCopied ? 'Copied!' : 'Invite'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );

  // ── Mobile drawer ─────────────────────────────────────────────────────────
  const renderMobileDrawer = () => (
    <Drawer
      anchor="bottom"
      open={drawerOpen}
      onClose={handleDrawerToggle}
      sx={{ '& .MuiDrawer-paper': { bottom: `${APPSHELL_HEIGHT}px` } }}
      ModalProps={{
        BackdropProps: {
          sx: { backdropFilter: 'blur(4px)', backgroundColor: alpha('#000', 0.3) },
          timeout: 300,
        },
      }}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 20, borderTopRightRadius: 20,
          maxHeight: `calc(100vh - ${APPSHELL_HEIGHT}px)`,
          bgcolor: 'background.default',
          boxShadow: '0 -4px 32px rgba(0,0,0,0.15)',
        }
      }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Box sx={{ width: 40, height: 4, bgcolor: 'divider', borderRadius: 2, mx: 'auto', mb: 2 }} />
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>Quick Actions</Typography>
          <IconButton onClick={handleDrawerToggle} size="small" sx={{ bgcolor: alpha(theme.palette.text.primary, 0.05) }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Divider sx={{ mb: 1 }} />

      <List sx={{ px: 1, pb: 2 }}>
        {/* Invite Creators — referral link */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleDrawerAction(handleCopyCreatorInvite)}
            disabled={inviteLoading}
            sx={{ borderRadius: 2, py: 1.5, px: 2, mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 44 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {inviteCopied
                  ? <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                  : <PersonAddIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                }
              </Box>
            </ListItemIcon>
            <ListItemText
              primary={inviteCopied ? "Link Copied!" : "Invite Creators"}
              secondary="Copy your creator referral link"
              primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem', color: inviteCopied ? 'success.main' : 'inherit' }}
              secondaryTypographyProps={{ fontSize: '0.75rem' }}
            />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton onClick={() => handleDrawerAction(onCopyLink)} sx={{ borderRadius: 2, py: 1.5, px: 2, mb: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 44 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ContentCopyIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              </Box>
            </ListItemIcon>
            <ListItemText
              primary="Copy Vendor Link"
              secondary="Share vendor signup link"
              primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
              secondaryTypographyProps={{ fontSize: '0.75rem' }}
            />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton onClick={() => handleDrawerAction(onOpenVendorDialog)} sx={{ borderRadius: 2, py: 1.5, px: 2, mb: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 44 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PersonAddIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              </Box>
            </ListItemIcon>
            <ListItemText
              primary="Add Vendor Manually"
              secondary="Enter details yourself"
              primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
              secondaryTypographyProps={{ fontSize: '0.75rem' }}
            />
          </ListItemButton>
        </ListItem>

        <Divider sx={{ my: 1, mx: 2 }} />

        <ListItem disablePadding>
          <ListItemButton onClick={() => handleDrawerAction(() => navigate('/user'))} sx={{ borderRadius: 2, py: 1.5, px: 2 }}>
            <ListItemIcon sx={{ minWidth: 44 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: alpha(theme.palette.text.primary, 0.05), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <SettingsIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </Box>
            </ListItemIcon>
            <ListItemText
              primary="Settings"
              secondary="Manage account"
              primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
              secondaryTypographyProps={{ fontSize: '0.75rem' }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );

  return (
    <HeaderSection>
      <Box sx={{
        display: 'flex',
        justifyContent: isMobile ? 'center' : 'space-between',
        alignItems: 'center',
        mb: isMobile ? 0 : 3,
        position: 'relative',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, mb: 0.25, letterSpacing: '-0.02em' }}
            >
              Money
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: 'block', fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              {isMobile ? 'Financial Control' : 'The Future of Work is Here'}
            </Typography>
          </Box>
          {!isMobile && renderDesktopActions()}
        </Box>

        {isMobile && (
          <Box sx={{ position: 'absolute', right: 0, top: 0 }}>
            <IconButton
              onClick={handleDrawerToggle}
              sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 2, width: 40, height: 40 }}
            >
              <MoreVertIcon sx={{ color: 'primary.main' }} />
            </IconButton>
            {totalIncomingAmount > 0 && (
              <StyledBadge
                badgeContent={totalIncomingAmount}
                color="error"
                sx={{
                  position: 'absolute', top: -4, right: -4, pointerEvents: 'none',
                  '& .MuiBadge-badge': { fontSize: '0.7rem', height: 18, minWidth: 18, padding: '0 4px' }
                }}
              />
            )}
          </Box>
        )}
      </Box>

      {isMobile && renderMobileQuickActions()}
      {isMobile && renderMobileDrawer()}
    </HeaderSection>
  );
};

export default BlitzPayHeader;